import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Crown, DollarSign, Dumbbell, Shield, User, Briefcase, Eye, X, Settings, Star, Activity, Target, Check } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { CoachDashboard, DashboardViewToggle } from "@/components/CoachDashboard";
import CouponManager from "@/components/CouponManager";
import AffiliateManager from "@/components/AffiliateManager";
import { RetentionInsightsCard } from "@/components/RetentionInsightsCard";
import { AthleteDashboardView } from "@/components/athlete/AthleteComponents";
import { AthleteDashboardContent } from "@/components/athlete/AthleteDashboardContent";
import { AthleteNav } from "@/components/athlete/AthleteNav";

const AdminDashboard = () => {
  const { user } = useAuth();
  const [dashboardView, setDashboardView] = useState("coach");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [updatingRole, setUpdatingRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    // Safety timeout: if any request hangs longer than 15s, abort and stop spinner
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const opts = { credentials: "include", signal: controller.signal };
      const [statsRes, usersRes, txRes] = await Promise.all([
        fetch(`${API}/admin/dashboard`, opts),
        fetch(`${API}/admin/users`, opts),
        fetch(`${API}/admin/transactions`, opts)
      ]);

      const statsData = statsRes.ok ? await statsRes.json() : {};
      const usersData = usersRes.ok ? await usersRes.json() : { users: [] };
      const txData = txRes.ok ? await txRes.json() : { transactions: [] };

      setStats(statsData);
      setUsers(usersData.users || []);
      setTransactions(txData.transactions || []);
    } catch (error) {
      console.error("[AdminDashboard] fetchData failed:", error);
      if (error.name === "AbortError") {
        toast.error("Loading took too long. Please refresh.");
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    setUpdatingRole(userId);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole })
      });
      
      if (response.ok) {
        toast.success("User role updated!");
        setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
      } else {
        toast.error("Failed to update role");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setUpdatingRole(null);
    }
  };

  const fetchUserDetails = async (userId) => {
    setLoadingDetails(true);
    try {
      const [userRes, workoutsRes, maxesRes] = await Promise.all([
        fetch(`${API}/admin/users/${userId}`, { credentials: "include" }),
        fetch(`${API}/admin/users/${userId}/workouts`, { credentials: "include" }),
        fetch(`${API}/admin/users/${userId}/maxes`, { credentials: "include" })
      ]);
      
      const userData = userRes.ok ? await userRes.json() : null;
      const workoutsData = workoutsRes.ok ? await workoutsRes.json() : { workouts: [] };
      const maxesData = maxesRes.ok ? await maxesRes.json() : { maxes: [] };
      
      setUserDetails({
        ...userData,
        workouts: workoutsData.workouts || [],
        maxes: maxesData.maxes || []
      });
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      toast.error("Failed to load user details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewUser = (u) => {
    setSelectedUser(u);
    fetchUserDetails(u.user_id);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // ── Athlete preview: render EXACTLY what an athlete sees on /dashboard.
  // Strips all admin chrome (header, role-toggle pills) and adds the
  // AthleteNav (bottom tab bar / desktop pills). The amber "PREVIEWING AS
  // ATHLETE" ribbon is the only admin affordance, with an Exit button.
  if (dashboardView === "athlete") {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        {/* Amber preview ribbon — the only admin chrome left in this mode */}
        <div className="fixed top-[64px] sm:top-[64px] left-0 right-0 z-40 bg-amber-500/95 backdrop-blur-sm border-b border-amber-400 px-4 py-2 flex items-center justify-center gap-3"
             data-testid="athlete-preview-pill">
          <Eye className="w-4 h-4 text-black" />
          <span className="text-xs sm:text-sm font-bold text-black uppercase tracking-wider">
            Previewing as athlete
          </span>
          <button
            onClick={() => setDashboardView("admin")}
            className="ml-2 text-xs font-bold text-black/70 hover:text-black underline"
            data-testid="exit-preview-btn">
            Exit preview
          </button>
        </div>
        {/* Match DashboardPage exactly: pt clears navbar+ribbon+pillnav,
            pb clears mobile bottom tab bar. */}
        <div className="pt-[112px] sm:pt-[156px] pb-24 sm:pb-12 px-6">
          <AthleteDashboardContent showOnboardingFlow={false} />
        </div>
        <AthleteNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header with View Toggle */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
                {dashboardView === "admin" && <><span className="text-destructive">ADMIN</span> DASHBOARD</>}
                {dashboardView === "coach" && <><span className="gradient-text">COACH</span> STUDIO</>}
                {dashboardView === "athlete" && <><span className="text-success">ATHLETE</span> VIEW</>}
              </h1>
              <DashboardViewToggle currentView={dashboardView} onViewChange={setDashboardView} />
            </div>

            {/* Admin View */}
            {dashboardView === "admin" && (
              <>
                {/* Tabs */}
                <div className="flex gap-4 mb-8 border-b border-border pb-4 overflow-x-auto">
                  {["overview", "users", "transactions", "coupons", "affiliates"].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-2 rounded-lg font-semibold capitalize transition-colors whitespace-nowrap ${
                        activeTab === tab ? "bg-primary text-white" : "text-text-secondary hover:text-white"
                      }`}
                      data-testid={`admin-tab-${tab}`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {/* Overview Tab */}
                {activeTab === "overview" && stats && (
                  <div className="space-y-8">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {[
                        { label: "Total Users", value: stats.total_users, icon: Users },
                        { label: "Active Subscriptions", value: stats.active_subscriptions, icon: Crown },
                        { label: "Total Revenue", value: `$${stats.total_revenue}`, icon: DollarSign },
                        { label: "Total Workouts", value: stats.total_workouts, icon: Dumbbell },
                      ].map((stat, i) => (
                        <div key={i} className="bg-surface border border-border rounded-xl p-6" data-testid={`admin-stat-${i}`}>
                          <stat.icon className="w-8 h-8 text-primary mb-3" />
                          <p className="text-text-muted text-sm">{stat.label}</p>
                          <p className="font-heading text-3xl font-bold">{stat.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Tier Breakdown */}
                    <div className="bg-surface border border-border rounded-xl p-6">
                      <h3 className="font-heading text-xl font-bold mb-4">SUBSCRIPTION BREAKDOWN</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(stats.tier_breakdown || {}).map(([tier, count]) => (
                          <div key={tier} className="text-center">
                            <p className="text-2xl font-bold text-primary">{count}</p>
                            <p className="text-text-secondary capitalize">{tier}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Retention Insights — Hormozi pattern-match */}
                    <RetentionInsightsCard />
                  </div>
                )}

                {/* Users Tab */}
                {activeTab === "users" && (
                  <div className="bg-surface border border-border rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-border flex justify-between items-center">
                      <h3 className="font-semibold">All Users ({users.length})</h3>
                      <div className="flex gap-2">
                        <span className="text-xs bg-success/20 text-success px-2 py-1 rounded">
                          {users.filter(u => u.subscription_status === "active").length} active
                        </span>
                        <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded">
                          {users.filter(u => u.role === "coach").length} coaches
                        </span>
                        <span className="text-xs bg-destructive/20 text-destructive px-2 py-1 rounded">
                          {users.filter(u => u.role === "admin").length} admins
                        </span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-[#0B0F1A]">
                          <tr>
                            <th className="text-left p-4 font-semibold">User</th>
                            <th className="text-left p-4 font-semibold">Email</th>
                            <th className="text-left p-4 font-semibold">Subscription</th>
                            <th className="text-left p-4 font-semibold">Role</th>
                            <th className="text-left p-4 font-semibold">Joined</th>
                            <th className="text-left p-4 font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((u, i) => (
                            <tr key={u.user_id} className="border-t border-border hover:bg-[#0B0F1A]" data-testid={`user-row-${i}`}>
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    u.role === "admin" ? "bg-destructive/20" : 
                                    u.role === "coach" ? "bg-amber-500/20" : "bg-primary/20"
                                  }`}>
                                    {u.role === "admin" ? <Shield className="w-5 h-5 text-destructive" /> :
                                     u.role === "coach" ? <Briefcase className="w-5 h-5 text-amber-400" /> :
                                     <User className="w-5 h-5 text-primary" />}
                                  </div>
                                  <div>
                                    <span className="block">{u.name}</span>
                                    <span className="text-xs text-text-muted">{u.user_id.slice(0, 12)}...</span>
                                  </div>
                                </div>
                              </td>
                              <td className="p-4 text-text-secondary">{u.email}</td>
                              <td className="p-4">
                                <div className="flex flex-col gap-1">
                                  <span className={`text-xs px-2 py-1 rounded w-fit ${
                                    u.subscription_status === "active" ? "bg-success/20 text-success" :
                                    u.subscription_status === "canceled" ? "bg-destructive/20 text-destructive" :
                                    "bg-[#2D3B4F] text-text-secondary"
                                  }`}>
                                    {u.subscription_status || "none"}
                                  </span>
                                  <span className="text-xs text-text-muted capitalize">{u.subscription_tier || "free"}</span>
                                </div>
                              </td>
                              <td className="p-4">
                                <select
                                  value={u.role}
                                  onChange={(e) => updateUserRole(u.user_id, e.target.value)}
                                  disabled={updatingRole === u.user_id || u.email === user?.email}
                                  className={`bg-[#1E293B] border border-[#1E293B] rounded-lg px-3 py-1.5 text-sm capitalize focus:border-primary outline-none ${
                                    updatingRole === u.user_id ? "opacity-50" : ""
                                  } ${u.email === user?.email ? "cursor-not-allowed opacity-50" : ""}`}
                                  data-testid={`role-select-${i}`}
                                >
                                  <option value="user">User</option>
                                  <option value="coach">Coach</option>
                                  <option value="admin">Admin</option>
                                </select>
                              </td>
                              <td className="p-4 text-text-secondary text-sm">
                                {new Date(u.created_at).toLocaleDateString()}
                              </td>
                              <td className="p-4">
                                <button
                                  onClick={() => handleViewUser(u)}
                                  className="text-text-muted hover:text-white p-2 rounded-lg hover:bg-[#1E293B]"
                                  title="View details"
                                  data-testid={`view-user-${i}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Transactions Tab */}
                {activeTab === "transactions" && (
                  <div className="bg-surface border border-border rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-border flex justify-between items-center">
                      <h3 className="font-semibold">Recent Transactions</h3>
                      <span className="text-sm text-text-secondary">
                        Total Revenue: <span className="text-success font-bold">${stats?.total_revenue || 0}</span>
                      </span>
                    </div>
                    {transactions.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-[#0B0F1A]">
                            <tr>
                              <th className="text-left p-4 font-semibold">Date</th>
                              <th className="text-left p-4 font-semibold">User</th>
                              <th className="text-left p-4 font-semibold">Amount</th>
                              <th className="text-left p-4 font-semibold">Type</th>
                              <th className="text-left p-4 font-semibold">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.map((tx, i) => (
                              <tr key={tx.transaction_id || i} className="border-t border-border" data-testid={`tx-row-${i}`}>
                                <td className="p-4 text-text-secondary text-sm">
                                  {new Date(tx.created_at).toLocaleDateString()}
                                </td>
                                <td className="p-4">{tx.user_email || tx.user_id?.slice(0, 12) || "Unknown"}</td>
                                <td className="p-4 text-success font-semibold">${tx.amount}</td>
                                <td className="p-4">
                                  <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded capitalize">
                                    {tx.type || "subscription"}
                                  </span>
                                </td>
                                <td className="p-4">
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    tx.status === "completed" ? "bg-success/20 text-success" :
                                    tx.status === "pending" ? "bg-amber-500/20 text-amber-400" :
                                    "bg-destructive/20 text-destructive"
                                  }`}>
                                    {tx.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-8 text-center">
                        <DollarSign className="w-12 h-12 text-text-muted mx-auto mb-4" />
                        <p className="text-text-secondary">No transactions yet</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Coupons Tab */}
                {activeTab === "coupons" && (
                  <CouponManager />
                )}

                {activeTab === "affiliates" && (
                  <AffiliateManager />
                )}

                {/* Workouts Tab */}
                {activeTab === "workouts" && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <p className="text-text-secondary">{stats?.total_workouts || 0} workouts in library</p>
                      <button 
                        onClick={() => setDashboardView("coach")}
                        className="bg-primary text-white px-4 py-2 rounded-lg flex items-center gap-2" 
                        data-testid="manage-content-btn"
                      >
                        <Settings className="w-5 h-5" />
                        Manage Content
                      </button>
                    </div>
                    <div className="bg-surface border border-border rounded-xl p-8 text-center">
                      <Dumbbell className="w-12 h-12 text-text-muted mx-auto mb-4" />
                      <p className="text-text-secondary">Switch to Coach view to manage workout content</p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Coach View */}
            {dashboardView === "coach" && <CoachDashboard />}

            {/* Athlete view is rendered above as an early-return — see top
                of the component. We keep the role-toggle pill clickable so
                users can flip back into preview from anywhere in admin. */}
          </motion.div>
        </div>
      </div>

      {/* User Detail Modal */}
      <AnimatePresence>
        {selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) { setSelectedUser(null); setUserDetails(null); } }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-surface border border-border rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              data-testid="user-detail-modal"
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-surface border-b border-border p-6 flex justify-between items-start z-10">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    selectedUser.role === "admin" ? "bg-destructive/20" : 
                    selectedUser.role === "coach" ? "bg-amber-500/20" : "bg-primary/20"
                  }`}>
                    {selectedUser.role === "admin" ? <Shield className="w-7 h-7 text-destructive" /> :
                     selectedUser.role === "coach" ? <Briefcase className="w-7 h-7 text-amber-400" /> :
                     <User className="w-7 h-7 text-primary" />}
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl font-bold">{selectedUser.name}</h2>
                    <p className="text-text-secondary">{selectedUser.email}</p>
                  </div>
                </div>
                <button 
                  onClick={() => { setSelectedUser(null); setUserDetails(null); }}
                  className="p-2 hover:bg-[#1E293B] rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {loadingDetails ? (
                <div className="p-12 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  {/* User Info Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-[#141B2D] rounded-xl p-4">
                      <p className="text-text-muted text-xs uppercase tracking-wide mb-1">Role</p>
                      <p className="font-semibold capitalize">{selectedUser.role}</p>
                    </div>
                    <div className="bg-[#141B2D] rounded-xl p-4">
                      <p className="text-text-muted text-xs uppercase tracking-wide mb-1">Subscription</p>
                      <p className="font-semibold capitalize">{selectedUser.subscription_tier || "Free"}</p>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        selectedUser.subscription_status === "active" ? "bg-success/20 text-success" : "bg-[#2D3B4F] text-text-muted"
                      }`}>
                        {selectedUser.subscription_status || "none"}
                      </span>
                    </div>
                    <div className="bg-[#141B2D] rounded-xl p-4">
                      <p className="text-text-muted text-xs uppercase tracking-wide mb-1">Joined</p>
                      <p className="font-semibold">{new Date(selectedUser.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="bg-[#141B2D] rounded-xl p-4">
                      <p className="text-text-muted text-xs uppercase tracking-wide mb-1">Workouts Completed</p>
                      <p className="font-semibold text-primary">{userDetails?.workouts?.length || 0}</p>
                    </div>
                  </div>

                  {/* User Maxes */}
                  {userDetails?.maxes && userDetails.maxes.length > 0 && (
                    <div>
                      <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">
                        <Target className="w-5 h-5 text-primary" />
                        Personal Records
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {userDetails.maxes.slice(0, 6).map((max, i) => (
                          <div key={i} className="bg-[#1E293B]/30 border border-[#1E293B]/50 rounded-lg p-3 flex justify-between items-center">
                            <span className="text-sm truncate flex-1">{max.exercise_name || "Unknown Exercise"}</span>
                            <span className="font-bold text-amber-400 ml-2">{max.max_weight} lbs</span>
                          </div>
                        ))}
                      </div>
                      {userDetails.maxes.length > 6 && (
                        <p className="text-text-muted text-sm mt-2">+{userDetails.maxes.length - 6} more records</p>
                      )}
                    </div>
                  )}

                  {/* Recent Workouts */}
                  {userDetails?.workouts && userDetails.workouts.length > 0 && (
                    <div>
                      <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">
                        <Activity className="w-5 h-5 text-success" />
                        Recent Workouts
                      </h3>
                      <div className="space-y-2">
                        {userDetails.workouts.slice(0, 5).map((workout, i) => (
                          <div key={i} className="bg-[#1E293B]/30 border border-[#1E293B]/50 rounded-lg p-3 flex items-center gap-3">
                            <div className="w-10 h-10 bg-success/20 rounded-lg flex items-center justify-center">
                              <Check className="w-5 h-5 text-success" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{workout.workout_title || "Workout"}</p>
                              <div className="flex items-center gap-3 text-xs text-text-muted">
                                <span>{new Date(workout.completed_at).toLocaleDateString()}</span>
                                {workout.duration_minutes && <span>{workout.duration_minutes} min</span>}
                                {workout.rating && (
                                  <span className="flex items-center gap-1">
                                    <Star className="w-3 h-3 text-amber-400" />
                                    {workout.rating}/5
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {userDetails.workouts.length > 5 && (
                        <p className="text-text-muted text-sm mt-2">+{userDetails.workouts.length - 5} more workouts</p>
                      )}
                    </div>
                  )}

                  {/* Empty State */}
                  {(!userDetails?.workouts || userDetails.workouts.length === 0) && 
                   (!userDetails?.maxes || userDetails.maxes.length === 0) && (
                    <div className="text-center py-8">
                      <User className="w-12 h-12 text-text-muted mx-auto mb-3" />
                      <p className="text-text-secondary">No workout activity yet</p>
                    </div>
                  )}

                  {/* User ID */}
                  <div className="pt-4 border-t border-border">
                    <p className="text-xs text-text-muted">
                      User ID: <code className="bg-[#1E293B] px-2 py-0.5 rounded">{selectedUser.user_id}</code>
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


export default AdminDashboard;
